import Component from '@glimmer/component'
import { reportingTitle, reportingToolTip as toolTipConfig } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import { BaseHighcharterComponent } from 'teacher-ui-client/components/base/highcharter'
import type * as Highcharts from 'highcharts'

export interface SeriesColumnOptions extends Highcharts.SeriesColumnOptions {
  category?: string
  name?: string
  color?: string
}

export type ChartSpacing = [number, number, number, number]

interface Args {
  categories?: string[]
  chartMargin?: (number | null)[]
  chartSpacing?: ChartSpacing
  colors?: string[]
  colorByPoint?: boolean
  dataLabelsEnabled?: boolean
  data: SeriesColumnOptions[]
  dataLabelsFormatter?: Highcharts.DataLabelsFormatterCallbackFunction
  legend?: Highcharts.LegendOptions
  stackingStyle?: Highcharts.OptionsStackingValue
  title?: Highcharts.TitleOptions | string
  toolTip?: Highcharts.TooltipOptions
  toolTipUnit?: string
  toolTipFormatter?: Highcharts.TooltipFormatterCallbackFunction
  xAxisLabel?: string
  xAxisLabelFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction
  xAxisPlotBands?: Highcharts.XAxisPlotBandsOptions[]
  xAxisPlotLines?: Highcharts.XAxisPlotLinesOptions[]
  xAxisTickWidth?: number
  xAxisVisible?: boolean
  yAxis?: Highcharts.YAxisOptions
  yAxisEndOnTick?: boolean
  yAxisGridLineColor?: string
  yAxisLabel?: string
  yAxisLabelFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction
  yAxisPlotBands?: Highcharts.YAxisPlotBandsOptions[]
  yAxisPlotLines?: Highcharts.YAxisPlotLinesOptions[]
  yAxisReversedStacks?: boolean
  yAxisTickInterval?: number
  yAxisVisible?: boolean
  yRange?: [number | null, number | null]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

const disabled = { enabled: false }

export class SeriesColumn extends Component<Signature> {
  get chartMargins() {
    if (!this.args.chartMargin) return { margin: undefined }

    const [marginTop, marginRight, marginBottom, marginLeft] = this.args.chartMargin

    return {
      marginTop: marginTop ?? undefined,
      marginRight: marginRight ?? undefined,
      marginBottom: marginBottom ?? undefined,
      marginLeft: marginLeft ?? undefined,
    }
  }

  get chartOptions(): Highcharts.Options {
    const tooltip = this.toolTip || disabled

    const options: Highcharts.Options = {
      chart: {
        type: 'column',
        plotBackgroundColor: this.plotBackgroundColor,
        backgroundColor: 'transparent',
        spacing: this.chartSpacing,
        ...this.chartMargins,
      },
      credits: disabled,
      title: this.chartTitle,
      yAxis: this.yAxis,
      xAxis: this.xAxis,
      plotOptions: this.plotOptions,
      tooltip,
      legend: this.args.legend ?? disabled,
    }
    if (this.colors) options.colors = this.colors

    return options
  }

  get chartSpacing() {
    return this.args.chartSpacing ?? [10, 10, 0, 10]
  }

  get chartTitle() {
    const title = this.args.title === undefined ? '' : this.args.title
    if (typeof title === 'string') {
      return reportingTitle(title)
    }
    return title
  }

  get colors() {
    return this.args.colors
  }

  get dataLabelsEnabled() {
    return this.args.dataLabelsEnabled ?? false
  }

  get highchartOptions() {
    return { ...this.chartOptions, series: this.args.data }
  }

  plotBackgroundColor = colors.dustyBlack50

  get dataLabels(): Highcharts.DataLabelsOptions {
    // Only set formatter if it's defined, else it will override highcharts' default and error for charts that enable it
    if (this.args.dataLabelsFormatter) {
      return {
        enabled: this.dataLabelsEnabled,
        formatter: this.args.dataLabelsFormatter,
      }
    }
    return { enabled: this.dataLabelsEnabled }
  }

  get plotOptions(): Highcharts.PlotOptions {
    return {
      series: {
        dataLabels: this.dataLabels,
      },
      column: {
        borderWidth: 0,
        stacking: this.args.stackingStyle,
        groupPadding: 0.2,
        pointPadding: 0.1,
        colorByPoint: this.args.colorByPoint ?? false,
      },
    }
  }

  get toolTip(): Highcharts.TooltipOptions {
    if (this.args.toolTip) return this.args.toolTip

    return toolTipConfig('y', this.toolTipUnit, {
      formatter: this.toolTipFormatter,
      backgroundColor: colors.neutral400,
    })
  }

  get toolTipFormatter() {
    return this.args.toolTipFormatter
  }

  get toolTipUnit() {
    return this.args.toolTipUnit ?? ''
  }

  get xAxis() {
    const xAxis: Highcharts.XAxisOptions = {
      title: reportingTitle(this.xAxisLabel),
      categories: this.args.categories,
      visible: this.xAxisVisible,
      allowDecimals: false,
      tickWidth: this.xAxisTickWidth,
      plotLines: this.xAxisPlotLines,
      plotBands: this.xAxisPlotBands,
      lineColor: colors.dustyBlack200,
      tickColor: colors.dustyBlack200,
    }

    if (this.xAxisLabelFormatter) {
      xAxis.labels = {
        formatter: this.xAxisLabelFormatter,
      }
    }

    return xAxis
  }

  get xAxisLabel() {
    return this.args.xAxisLabel ?? ''
  }

  get xAxisLabelFormatter() {
    return this.args.xAxisLabelFormatter ?? null
  }

  get xAxisPlotBands() {
    return this.args.xAxisPlotBands ?? []
  }

  get xAxisPlotLines() {
    return this.args.xAxisPlotLines ?? []
  }

  get xAxisTickWidth() {
    return this.args.xAxisTickWidth ?? 1
  }

  get xAxisVisible() {
    return this.args.xAxisVisible ?? true
  }

  get yAxis() {
    if (this.args.yAxis) return this.args.yAxis

    const [min, max] = this.yRange

    const { yAxisLabelFormatter } = this

    const yAxis: Highcharts.YAxisOptions = {
      title: reportingTitle(this.yAxisLabel),
      visible: this.yAxisVisible,
      min,
      max,
      plotLines: this.yAxisPlotLines,
      plotBands: this.yAxisPlotBands,
      reversedStacks: this.yAxisReversedStacks,
      allowDecimals: false,
      gridLineColor: this.yAxisGridLineColor,
      endOnTick: this.yAxisEndOnTick,
      tickInterval: this.yAxisTickInterval,
    }

    if (yAxisLabelFormatter) {
      yAxis.labels = {
        formatter: yAxisLabelFormatter,
      }
    }

    return yAxis
  }

  get yAxisEndOnTick() {
    return this.args.yAxisEndOnTick ?? true
  }

  get yAxisLabel() {
    return this.args.yAxisLabel ?? ''
  }

  get yAxisLabelFormatter() {
    return this.args.yAxisLabelFormatter ?? null
  }

  get yAxisGridLineColor() {
    return this.args.yAxisGridLineColor ?? colors.dustyBlack100
  }

  get yAxisPlotBands() {
    return this.args.yAxisPlotBands ?? []
  }

  get yAxisPlotLines() {
    return this.args.yAxisPlotLines ?? []
  }

  get yAxisReversedStacks() {
    return this.args.yAxisReversedStacks ?? true
  }

  get yAxisTickInterval() {
    return this.args.yAxisTickInterval
  }

  get yAxisVisible() {
    return this.args.yAxisVisible ?? true
  }

  get yRange() {
    return this.args.yRange ?? []
  }

  <template><BaseHighcharterComponent class="h-full" @options={{this.highchartOptions}} ...attributes /></template>
}

export default SeriesColumn
